import "./index.css";

import { getThemeStyles } from "@atlaskit/tokens";
import AnalyticsProviders from "@atlassian/wac-analytics-providers";
import { InitUFO } from "@atlassian/wac-init-ufo";
import getUFORouteName from "@atlassian/wac-ufo-route-name";
import UFOSegment from "@atlassian/wac-ufo-segment";
import traceUFOPageLoad from "@atlassian/wac-ufo-trace-pageload";
import { MeResponse } from "@atlassiansox/asc-stargate";

import { InitializeAutoAtlAnalytics } from "./analytics/atl-analytics";
import {
    WacAnalyticsClientProvider,
    WacAnalyticsListener
} from "./analytics/wac-analytics";
import { AppPricingData } from "./contentful";
import {
    AvailableProductsResponse,
    BxpWebStargateClientProvider,
    GetSignupUserTraitsResponse
} from "./libraries/bxp-web-stargate-client/src";
import { LocationProvider } from "./utils/location-provider";
import { LoggedInUserProvider } from "./utils/logged-in-user-provider";
import { OneTrustProvider } from "./utils/onetrust-provider";
import { UserLocale } from "./utils/useGeoCurrency";
import { parseRoute } from "./wac-routes";

import type {
    Template,
    TemplateCollection
} from "./content/templates/template.types";

export type LayoutProps = {
    location: Location;
    pricing?: AppPricingData;
    availableProducts?: AvailableProductsResponse;
    signupUserTraits?: GetSignupUserTraitsResponse;
    userProfile?: MeResponse;
    countryCode?: UserLocale;
    templates?: Template[];
    collections?: TemplateCollection[];
    themeStyles?: Awaited<ReturnType<typeof getThemeStyles>>;
    children: React.ComponentType<PageProps>;
};

export type PageProps = {
    pricing?: AppPricingData;
    availableProducts?: AvailableProductsResponse;
    signupUserTraits?: GetSignupUserTraitsResponse;
    userProfile?: MeResponse;
    countryCode?: UserLocale;
    templates?: Template[];
    collections?: TemplateCollection[];
};

export const Layout = (props: LayoutProps) => {
    const hasAutoAtlAnalytics =
        process.env["AUTO_ANALYTICS_ENABLED"] === "true";
    const pageViewProperties: { originProduct?: string } = {};
    const { path } = parseRoute(location);
    const isBrowser = () => typeof window !== "undefined";
    if (isBrowser()) {
        const ufoName = getUFORouteName(path.length ? "/" + path : "home");
        traceUFOPageLoad(ufoName, ufoName); // (ufoName, route.name);

        pageViewProperties.originProduct =
            "wac" +
            (window.location.pathname === "/"
                ? ""
                : window.location.pathname.replace(new RegExp("/", "g"), "-"));
    }

    return (
        <OneTrustProvider>
            <InitUFO />
            <LocationProvider location={props.location}>
                <UFOSegment name="wac">
                    <BxpWebStargateClientProvider
                        baseUrl={
                            process.env[
                                "NEXT_PUBLIC_API_PRIVATE_BASE_URL"
                            ] as string
                        }
                    >
                        <LoggedInUserProvider>
                            <WacAnalyticsClientProvider>
                                <WacAnalyticsListener>
                                    <AnalyticsProviders>
                                        <props.children {...props} />
                                    </AnalyticsProviders>
                                    {/**
                                     * InitializeAutoAtlAnalytics is used to auto-capture GTM events (previously handled
                                     * by the atl-analytics GTM wrapper). The contents of that wrapper have been ported into
                                     * this repository.
                                     *
                                     * Note: this will only attach auto-analytics to elements that exist in the page
                                     * when this component is mounted, so it needs to be the last component to render.
                                     *
                                     * In its current state, this will only pick up the components that existed on
                                     * after the first render of the above Component.
                                     *
                                     * You can call Initializer.autoTrack() in a useEffect or useLayoutEffect
                                     * to attach listeners to any new buttons/links/etc. that were added dynamically.
                                     */}
                                    {hasAutoAtlAnalytics && (
                                        <InitializeAutoAtlAnalytics
                                            pageViewProperties={
                                                pageViewProperties
                                            }
                                        />
                                    )}
                                </WacAnalyticsListener>
                            </WacAnalyticsClientProvider>
                        </LoggedInUserProvider>
                    </BxpWebStargateClientProvider>
                </UFOSegment>
            </LocationProvider>
        </OneTrustProvider>
    );
};
